import { DateTime } from "luxon";
import { Timestamp } from "firebase/firestore";

/**
 * USVI Timezone
 */
export const USVI_TIMEZONE = 'America/Virgin';

/**
 * Converts a Firestore Timestamp to a Luxon DateTime object.
 * @param {Timestamp} timestamp - Firestore Timestamp object.
 * @returns {DateTime} - Luxon DateTime object.
 */
export function fromFirestoreTimestamp(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) {
    return DateTime.fromJSDate(timestamp.toDate());
  }
  // Handle other formats if necessary
  return DateTime.fromISO(new Date(timestamp).toISOString());
}

/**
 * Formats a Luxon DateTime object to a human-readable string.
 * @param {DateTime} dateTime - Luxon DateTime object.
 * @returns {string} - Formatted date string (e.g., 'Jan 1, 2024').
 */
export function formatDateForDisplay(dateTime) {
  if (!dateTime) return "N/A";
  return dateTime.toLocaleString(DateTime.DATE_MED);
}

/**
 * Formats a Luxon DateTime object for input fields (YYYY-MM-DD).
 * @param {DateTime} dateTime - Luxon DateTime object.
 * @returns {string} - Formatted date string for input.
 */
export function formatDateForInput(dateTime) {
  if (!dateTime) return "";
  return dateTime.toISODate();
}

/**
 * Parses a date string from input (YYYY-MM-DD) to a Luxon DateTime object.
 * @param {string} dateString - Date string from input.
 * @returns {DateTime} - Luxon DateTime object.
 */
export function parseDateFromInput(dateString) {
  return DateTime.fromISO(dateString);
}

/**
 * Validates if a date string is in a correct format.
 * @param {string} dateString - Date string to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
export function isValidDate(dateString) {
  const dateTime = DateTime.fromISO(dateString);
  return dateTime.isValid;
}

/**
 * Converts a date input to Firestore Timestamp.
 * @param {string} dateString - Date string from input.
 * @returns {Timestamp} - Firestore Timestamp object.
 */
export function toFirestoreTimestamp(input) {
  if (!input) return null;

  // If already a Date object:
  if (input instanceof Date) {
    return Timestamp.fromDate(input);
  }

  // If input is a string, parse using Luxon:
  const dateTime = DateTime.fromISO(input);
  if (!dateTime.isValid) return null;

  return Timestamp.fromDate(dateTime.toJSDate());
}

/**
 * Day name mapping for business hours
 */
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const SHORT_DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Parse time string (e.g., "9:00 AM", "14:00", "9:00") into hours and minutes
 * @param {string} timeStr - Time string
 * @returns {object|null} - { hours, minutes } or null if invalid
 */
export function parseTimeString(timeStr) {
  if (!timeStr) return null;
  
  const normalizedTime = timeStr.trim().toLowerCase();
  
  // Handle 12-hour format (9:00 AM, 9:00am, 9 AM)
  const twelveHourMatch = normalizedTime.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i);
  if (twelveHourMatch) {
    let hours = parseInt(twelveHourMatch[1], 10);
    const minutes = twelveHourMatch[2] ? parseInt(twelveHourMatch[2], 10) : 0;
    const period = twelveHourMatch[3]?.toLowerCase();
    
    if (period === 'pm' && hours !== 12) {
      hours += 12;
    } else if (period === 'am' && hours === 12) {
      hours = 0;
    }
    
    return { hours, minutes };
  }
  
  // Handle 24-hour format (14:00, 9:00)
  const twentyFourMatch = normalizedTime.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourMatch) {
    return {
      hours: parseInt(twentyFourMatch[1], 10),
      minutes: parseInt(twentyFourMatch[2], 10)
    };
  }
  
  return null;
}

/**
 * Check if a business is currently open based on their hours
 * @param {object} businessHours - Business hours object (various formats supported)
 * @param {Date} checkTime - Time to check (defaults to now in USVI timezone)
 * @returns {object} - { isOpen, opensAt, closesAt, nextOpenDay }
 */
export function isBusinessOpen(businessHours, checkTime = null) {
  // Default result
  const result = {
    isOpen: false,
    currentDayOpen: null,
    currentDayClose: null,
    opensAt: null,
    closesAt: null,
    status: 'unknown',
    nextOpenDay: null
  };
  
  if (!businessHours) {
    result.status = 'no_hours';
    return result;
  }
  
  // Get current time in USVI timezone
  const now = checkTime 
    ? DateTime.fromJSDate(checkTime).setZone(USVI_TIMEZONE)
    : DateTime.now().setZone(USVI_TIMEZONE);
  
  const currentDayIndex = now.weekday % 7; // Luxon: 1=Mon, 7=Sun; we need 0=Sun, 6=Sat
  const currentDayName = DAY_NAMES[currentDayIndex === 0 ? 6 : currentDayIndex - 1];
  const shortDayName = SHORT_DAY_NAMES[currentDayIndex === 0 ? 6 : currentDayIndex - 1];
  const currentHour = now.hour;
  const currentMinute = now.minute;
  
  // Support various hour formats:
  // 1. Array format: [{ day: 'monday', open: '9:00', close: '17:00' }, ...]
  // 2. Object format: { monday: { open: '9:00', close: '17:00' }, ... }
  // 3. Simple format: { monday: '9:00 AM - 5:00 PM', ... }
  
  let todayHours = null;
  
  if (Array.isArray(businessHours)) {
    // Array format
    todayHours = businessHours.find(h => 
      h.day?.toLowerCase() === currentDayName || 
      h.day?.toLowerCase() === shortDayName ||
      h.dayOfWeek === currentDayIndex
    );
  } else if (typeof businessHours === 'object') {
    // Object format
    const hourKeys = [currentDayName, shortDayName, currentDayIndex.toString()];
    for (const key of hourKeys) {
      if (businessHours[key]) {
        const hours = businessHours[key];
        if (typeof hours === 'string') {
          // Simple format: "9:00 AM - 5:00 PM"
          const parts = hours.split('-').map(s => s.trim());
          if (parts.length === 2) {
            todayHours = { open: parts[0], close: parts[1] };
          } else if (hours.toLowerCase() === 'closed') {
            todayHours = { closed: true };
          }
        } else {
          todayHours = hours;
        }
        break;
      }
    }
  }
  
  // Check if closed today
  if (!todayHours || todayHours.closed || todayHours.isClosed) {
    result.status = 'closed_today';
    // Find next open day
    result.nextOpenDay = findNextOpenDay(businessHours, currentDayIndex);
    return result;
  }
  
  // Parse open and close times
  const openTime = parseTimeString(todayHours.open || todayHours.openTime || todayHours.opens);
  const closeTime = parseTimeString(todayHours.close || todayHours.closeTime || todayHours.closes);
  
  if (!openTime || !closeTime) {
    result.status = 'invalid_hours';
    return result;
  }
  
  result.currentDayOpen = formatTime(openTime.hours, openTime.minutes);
  result.currentDayClose = formatTime(closeTime.hours, closeTime.minutes);
  
  // Convert to minutes for comparison
  const currentTimeMinutes = currentHour * 60 + currentMinute;
  const openTimeMinutes = openTime.hours * 60 + openTime.minutes;
  let closeTimeMinutes = closeTime.hours * 60 + closeTime.minutes;
  
  // Handle overnight hours (e.g., bar open 6 PM - 2 AM)
  const isOvernight = closeTimeMinutes < openTimeMinutes;
  
  if (isOvernight) {
    // For overnight hours, add 24 hours to close time for comparison
    closeTimeMinutes += 24 * 60;
    const adjustedCurrentTime = currentTimeMinutes < openTimeMinutes 
      ? currentTimeMinutes + 24 * 60 
      : currentTimeMinutes;
    
    result.isOpen = adjustedCurrentTime >= openTimeMinutes && adjustedCurrentTime < closeTimeMinutes;
  } else {
    result.isOpen = currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes;
  }
  
  if (result.isOpen) {
    result.status = 'open';
    result.closesAt = result.currentDayClose;
  } else if (currentTimeMinutes < openTimeMinutes) {
    result.status = 'opens_today';
    result.opensAt = result.currentDayOpen;
  } else {
    result.status = 'closed';
    result.nextOpenDay = findNextOpenDay(businessHours, currentDayIndex);
  }
  
  return result;
}

/**
 * Find the next open day for a business
 * @param {object} businessHours - Business hours object
 * @param {number} currentDayIndex - Current day index (0=Sun, 6=Sat)
 * @returns {string|null} - Next open day name or null
 */
function findNextOpenDay(businessHours, currentDayIndex) {
  if (!businessHours) return null;
  
  for (let i = 1; i <= 7; i++) {
    const checkDay = (currentDayIndex + i) % 7;
    const dayName = DAY_NAMES[checkDay];
    const shortName = SHORT_DAY_NAMES[checkDay];
    
    let dayHours = null;
    
    if (Array.isArray(businessHours)) {
      dayHours = businessHours.find(h => 
        h.day?.toLowerCase() === dayName || 
        h.day?.toLowerCase() === shortName
      );
    } else if (typeof businessHours === 'object') {
      dayHours = businessHours[dayName] || businessHours[shortName];
    }
    
    if (dayHours && !dayHours.closed && !dayHours.isClosed && dayHours !== 'closed') {
      return i === 1 ? 'Tomorrow' : dayName.charAt(0).toUpperCase() + dayName.slice(1);
    }
  }
  
  return null;
}

/**
 * Format time for display
 * @param {number} hours - Hours (0-23)
 * @param {number} minutes - Minutes (0-59)
 * @returns {string} - Formatted time (e.g., "9:00 AM")
 */
function formatTime(hours, minutes) {
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
}

/**
 * Get open/closed status text for display
 * @param {object} businessHours - Business hours object
 * @returns {object} - { text, className, isOpen }
 */
export function getOpenStatusText(businessHours) {
  const status = isBusinessOpen(businessHours);
  
  switch (status.status) {
    case 'open':
      return {
        text: `Open · Closes at ${status.closesAt}`,
        shortText: 'Open Now',
        className: 'status-open',
        isOpen: true
      };
    case 'opens_today':
      return {
        text: `Closed · Opens at ${status.opensAt}`,
        shortText: 'Opens Today',
        className: 'status-opens-soon',
        isOpen: false
      };
    case 'closed_today':
      return {
        text: status.nextOpenDay ? `Closed · Opens ${status.nextOpenDay}` : 'Closed Today',
        shortText: 'Closed',
        className: 'status-closed',
        isOpen: false
      };
    case 'closed':
      return {
        text: status.nextOpenDay ? `Closed · Opens ${status.nextOpenDay}` : 'Closed',
        shortText: 'Closed',
        className: 'status-closed',
        isOpen: false
      };
    default:
      return {
        text: 'Hours not available',
        shortText: '',
        className: 'status-unknown',
        isOpen: null
      };
  }
}

/**
 * Format a JS Date (or Firestore Timestamp) as a full date+time string in
 * USVI local time (America/Virgin, UTC-4 year-round).
 * Example output: "May 9, 2026, 3:42 PM"
 * @param {Date|import('firebase/firestore').Timestamp} dateOrTs
 * @returns {string}
 */
export function formatUSVITime(dateOrTs) {
  if (!dateOrTs) return '';
  const date = dateOrTs.toDate ? dateOrTs.toDate() : new Date(dateOrTs);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    timeZone: USVI_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a JS Date (or Firestore Timestamp) as a short date in USVI time.
 * Example output: "May 9"  (current year) or "May 9, 2025" (other year)
 * @param {Date|import('firebase/firestore').Timestamp} dateOrTs
 * @returns {string}
 */
export function formatShortDate(dateOrTs) {
  if (!dateOrTs) return '';
  const date = dateOrTs.toDate ? dateOrTs.toDate() : new Date(dateOrTs);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const opts = {
    timeZone: USVI_TIMEZONE,
    month: 'short',
    day: 'numeric',
  };
  // Include year only if it differs from the current year.
  const year = new Intl.DateTimeFormat('en-US', { timeZone: USVI_TIMEZONE, year: 'numeric' })
    .format(date);
  const currentYear = new Intl.DateTimeFormat('en-US', { timeZone: USVI_TIMEZONE, year: 'numeric' })
    .format(now);
  if (year !== currentYear) opts.year = 'numeric';
  return date.toLocaleString('en-US', opts);
}
